/**
 * Demo — React wrapper for the imperative `lib/pilot/demo.js`
 * canvas-takeover scenes.
 *
 * Phase 7 of the React-engine port (`docs/eng/pilot-react-engine.md`).
 *
 * Architecture: the canvas-DRAWING half of demo.js stays imperative —
 * canvas pixels are not React's domain, per the architectural-invariant
 * carve-out. What this component owns instead is the *lifecycle*:
 *
 *   - Mount the `<section id="demo">` once.
 *   - On effect, call `buildShell` (builds the scene scroller, the
 *     background graph, and the per-scene rendered content into the
 *     section), `wireSceneTracking` (IntersectionObserver), and
 *     `wireKeyboard` (Arrow / Page / Home / End → goToScene).
 *   - On unmount, call `teardownDemo` which clears the current scene's
 *     timers (each scene exposes `root.__cleanup`), disconnects the
 *     observer, removes the keyboard listener, and resets the engine's
 *     module-level state so the next mount rebuilds from scratch.
 *
 * The Pilot context's `viewMode === 'demo'` predicate guards this
 * component's mount upstream (in `DemoSection.tsx`), so this component
 * never sees a "wrong mode" render — it's either fully mounted with
 * an active engine, or not in the tree at all. That means `useEffect`
 * cleanup is the ONLY teardown path; the engine's body-class
 * `demo-mode` is owned by PilotProvider and not touched here.
 */

import React, {useEffect, useRef} from 'react';
import {bootDemo, teardownDemo} from '../../lib/pilot/demo.js';

export default function Demo() {
  const sectionRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!sectionRef.current) return;

    // `bootDemo` calls `buildShell` (which `document.getElementById`s
    // "#demo" to find its host) → `wireSceneTracking` (Intersection-
    // Observer) → `wireKeyboard` (Arrow/Page/Home/End → goToScene) →
    // `setActiveScene(1)`. The id lookup resolves to our ref-owned
    // <section> because React just mounted it with id="demo". Treated
    // as the canvas-exception carve-out's "imperative drawing into a
    // ref-owned canvas" — here the section *is* the canvas-takeover
    // ground the engine paints into.
    bootDemo();

    return () => {
      teardownDemo();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      // `isolate` (CSS `isolation: isolate`) forms a fresh stacking
      // context so demo.js's `.demo-dots` strip (which paints with
      // `z-50` and would otherwise land in the ROOT stacking context
      // since `.demo-shell` doesn't form one) stays contained inside
      // <section id="demo">. Without isolation, the navbar's z-10
      // can be beaten by descendant z-50 elements painting into the
      // same root context — even though they don't visually overlap
      // the navbar, that can intercept clicks in some browsers.
      className="demo demo-shell relative isolate min-h-screen snap-y snap-mandatory overflow-hidden bg-taupe-950 text-taupe-50"
      id="demo"
    />
  );
}
